import { useSelector } from 'react-redux';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { FEATURE, type FeatureName } from 'constant/features';
import { useHasFeature } from 'hooks/useHasFeature';
import { RootState } from 'redux/store';

interface FeatureProtectedRouteProps {
    feature: FeatureName;
    /** Default `/dashboard` (home for B2B). */
    redirectTo?: string;
}

/** Route guard for company entitlements. Does not replace role checks. */
export function FeatureProtectedRoute({
    feature,
    redirectTo = '/dashboard',
}: FeatureProtectedRouteProps) {
    const allowed = useHasFeature(feature);
    if (!allowed) {
        return <Navigate to={redirectTo} replace />;
    }
    return <Outlet />;
}

/**
 * Partner-organizations product routes. Own-company profile
 * (`/dashboard/:service_provider_id/organization`) stays reachable.
 */
export function PartnerOrganizationsRoute() {
    const allowed = useHasFeature(FEATURE.PARTNER_ORGANIZATIONS);
    const { id } = useParams();
    const ownServiceProviderId = useSelector(
        (state: RootState) => state.user.entity.service_provider_id
    );
    const isOwnCompany =
        id != null && ownServiceProviderId != null && String(id) === String(ownServiceProviderId);

    if (allowed || isOwnCompany) {
        return <Outlet />;
    }

    return <Navigate to="/dashboard" replace />;
}
